import { createHttpClient } from '@adapters/HttpClient';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { ProductApiAdapter } from '@shared/adapters/ProductApiAdapter';
import { CountriesApiAdapter } from '@shared/adapters/CountriesApiAdapter';
import { ProductService } from '@shared/services/ProductService';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaymentMethodsApiAdapter } from '../../adapters/PaymentMethodsApiAdapter';
import { ShopsApiAdapter } from '../../adapters/ShopsApiAdapter';
import { TeamsApiAdapter } from '../../adapters/TeamsApiAdapter';
import { useTags } from '../../hooks/useTags';
import type { CrmStatusModel } from '../../models/CRM';
import { CRMFiltersPanel } from './CRMFiltersPanel';
import { DownloadExcelButton } from './DownloadExcelButton';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  allColumns: any[];
  selectedColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  fixedColumns?: string[];
  searchTerm: string;
  /** Filtros actuales */
  teamIds?: string[];
  phoneSearchTerm?: string;
  statusId?: number;
  shopIds?: string[];
  tagId?: number;
  shippingCountryCodes?: string[];
  paymentMethodIds?: string[];
  productIds?: string[];
  dateFrom?: string;
  dateTo?: string;
  sorting?: { field: string; direction: 'asc' | 'desc' };
  statusList: CrmStatusModel[];
  /** Handlers de filtros */
  onStatusChange: (statusId: number | undefined) => void;
  onShopIdsChange: (shopIds: string[]) => void;
  onTagChange: (tagId: number | undefined) => void;
  onShippingCountryCodesChange: (codes: string[]) => void;
  onPaymentMethodChange: (paymentMethodIds: string[]) => void;
  onTeamIdsChange: (teamIds: string[]) => void;
  onPhoneSearchChange: (value: string) => void;
  onProductIdsChange: (productIds: string[]) => void;
  onDateRangeChange: (dateFrom: string, dateTo: string) => void;
  onClearFilters: () => void;
}

export const FiltersSection = ({
  handleSearch,
  allColumns,
  selectedColumns,
  onColumnsChange,
  fixedColumns = [],
  searchTerm,
  teamIds = [],
  phoneSearchTerm = '',
  statusId,
  shopIds = [],
  tagId,
  shippingCountryCodes = [],
  paymentMethodIds,
  productIds = [],
  dateFrom,
  dateTo,
  sorting,
  onShopIdsChange = () => {},
  onTagChange = () => {},
  onShippingCountryCodesChange = () => {},
  onPaymentMethodChange = () => {},
  onTeamIdsChange = () => {},
  onPhoneSearchChange = () => {},
  onProductIdsChange = () => {},
  onDateRangeChange = () => {},
  onClearFilters = () => {},
}: FiltersSectionProps) => {
  const { t } = useTranslation('crmModule');
  const [inputValue, setInputValue] = useState(searchTerm);

  const httpClient = createHttpClient(apiClientWH);
  const shopsAdapter = useMemo(() => new ShopsApiAdapter(httpClient), [httpClient]);
  const teamsAdapter = useMemo(() => new TeamsApiAdapter(httpClient), [httpClient]);
  const paymentMethodsAdapter = useMemo(() => new PaymentMethodsApiAdapter(httpClient), [httpClient]);
  const productService = useMemo(() => {
    const adapter = new ProductApiAdapter(httpClient);
    return new ProductService(adapter);
  }, [httpClient]);

  // Cargar métodos de pago
  const { data: paymentMethods = [], isLoading: isLoadingPaymentMethods } = useQuery({
    queryKey: ['crm-filters-payment-methods'],
    queryFn: () => paymentMethodsAdapter.getAll(),
  });

  // Función para cargar clientes con paginación y búsqueda (para MultiSelect dinámico)
  const loadTeamsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const teams = await teamsAdapter.getTeamsChild(params.limit);
        const options = teams.map((team) => ({
          value: String(team.id),
          label: team.name,
        }));
        return {
          options,
          hasMore: false,
        };
      } catch (error) {
        console.error('Error loading teams:', error);
        return { options: [], hasMore: false };
      }
    },
    [teamsAdapter],
  );

  // Función para cargar países con paginación y búsqueda (para MultiSelect dinámico)
  const loadCountriesForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        void params.page;
        const countriesData = await new CountriesApiAdapter(httpClient).getCountries();
        const options = countriesData.map((country) => ({
          value: country.code,
          label: country.name,
        }));
        return {
          options,
          hasMore: false,
        };
      } catch (error) {
        console.error('Error loading countries:', error);
        return { options: [], hasMore: false };
      }
    },
    [httpClient],
  );

  // Función para cargar tiendas con paginación y búsqueda (para MultiSelect dinámico)
  const loadShopsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const result = await shopsAdapter.getShopsPaginated({
          search: params.search,
          page: params.page,
          limit: params.limit,
          includeTeam: true, // Siempre incluir team en CRM ya que usamos crm_managed_by=tenant_3pl
        });

        const options = result.shops.map((shop) => {
          // Construir descripción con nombre del team si aplica
          const description = shop.team?.name ? shop.team.name : undefined;
          return {
            value: shop.id,
            label: shop.name,
            description,
          };
        });

        return {
          options,
          hasMore: result.hasMore,
        };
      } catch (error) {
        console.error('Error loading shops:', error);
        return { options: [], hasMore: false };
      }
    },
    [shopsAdapter],
  );

  // Cargar productos con paginación y búsqueda para el MultiSelect (igual que en Orders)
  const loadProductsForMultiSelect = useCallback(
    async (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> => {
      try {
        const result = await productService.getProductsPaginated({
          searchTerm: params.search,
          page: params.page,
          limit: params.limit,
          fields: 'basic',
        });
        const options = result.products.map((product) => ({
          value: product.id.toString(),
          label: product.name || `Producto ${product.id}`,
          description: product.code ? product.code : undefined,
        }));
        return { options, hasMore: result.hasMore };
      } catch (error) {
        console.error('Error loading products:', error);
        return { options: [], hasMore: false };
      }
    },
    [productService],
  );

  const { tags: tagsList = [], isLoading: isLoadingTags } = useTags({ per_page: 100 }, true);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSearchSubmit = () => {
    const trimmedValue = inputValue.trim();
    handleSearch(trimmedValue);
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (teamIds.length > 0) count++;
    if (phoneSearchTerm?.trim()) count++;
    if (shopIds.length > 0) count++;
    if (tagId !== undefined) count++;
    if (shippingCountryCodes.length > 0) count++;
    if (paymentMethodIds && paymentMethodIds.length > 0) count++;
    if (productIds && productIds.length > 0) count++;
    if (dateFrom && dateTo) count++;
    return count;
  }, [teamIds, phoneSearchTerm, shopIds, tagId, shippingCountryCodes, paymentMethodIds, productIds, dateFrom, dateTo]);

  const handleDateRangeChange = (from: string, to: string) => {
    onDateRangeChange(from, to);
  };

  const filtersTitle = t('filters.title', 'Filtros');

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />

      <FiltersDropdown
        title={filtersTitle}
        triggerLabel={filtersTitle}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
      >
        <CRMFiltersPanel
          teamIds={teamIds}
          phoneSearchTerm={phoneSearchTerm}
          shopIds={shopIds}
          tagId={tagId}
          shippingCountryCodes={shippingCountryCodes}
          paymentMethodIds={paymentMethodIds}
          productIds={productIds}
          dateFrom={dateFrom}
          dateTo={dateTo}
          loadTeamsForMultiSelect={loadTeamsForMultiSelect}
          loadShopsForMultiSelect={loadShopsForMultiSelect}
          loadProductsForMultiSelect={loadProductsForMultiSelect}
          loadCountriesForMultiSelect={loadCountriesForMultiSelect}
          paymentMethodOptions={paymentMethods}
          tags={tagsList}
          isLoadingPaymentMethods={isLoadingPaymentMethods}
          isLoadingTags={isLoadingTags}
          onShopIdsChange={onShopIdsChange}
          onTagChange={onTagChange}
          onShippingCountryCodesChange={onShippingCountryCodesChange}
          onPaymentMethodChange={onPaymentMethodChange}
          onTeamIdsChange={onTeamIdsChange}
          onPhoneSearchChange={onPhoneSearchChange}
          onProductIdsChange={onProductIdsChange}
          onDateRangeChange={handleDateRangeChange}
          onClearAll={onClearFilters}
        />
      </FiltersDropdown>

      <ColumnSelector
        columns={allColumns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        fixedColumns={fixedColumns}
        storageKey="crm-list"
        dataTestId="crm-column-selector"
      />
      <DownloadExcelButton
        search={searchTerm}
        teamIds={teamIds}
        statusId={statusId}
        shopIds={shopIds}
        tagId={tagId}
        shippingCountryCodes={shippingCountryCodes}
        paymentMethodIds={paymentMethodIds}
        productIds={productIds}
        dateFrom={dateFrom}
        dateTo={dateTo}
        sorting={sorting}
        visibleColumns={selectedColumns}
      />
    </>
  );
};
